import { CopyOutlined, FileTextOutlined } from '@ant-design/icons';
import {
  Button,
  Input,
  message,
  Modal,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { getCrmLink, isHTML } from './utils';
import { sanitizeHTML } from '../../utils';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Stage } from '../../types';

interface Note {
  id: number | string;
  body: string;
  subject?: string;
  createdBy?: string;
  updatedBy?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: any;
}

interface Lead {
  id: string;
  source_id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  last_activity?: string;
  stage: string;
  notes?: string | Note[];
}

interface LeadSectionProps {
  apiUrl: string;
  agentId: string;
  accessToken: string;
  clientConfig: any;
  setError: (error: string | undefined) => void;
  stages: Stage[];
}

export function Leads({
  apiUrl,
  agentId,
  accessToken,
  setError,
  clientConfig,
  stages,
}: LeadSectionProps) {
  const [loading, setLoading] = useState<boolean>(true);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [savingStageLeadId, setSavingStageLeadId] = useState<string | null>(
    null,
  );
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [visibleNotesCount, setVisibleNotesCount] = useState<number>(15);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState<string>(() => {
    return searchParams.get('lead') || '';
  });
  const location = useLocation();
  const stageOptions = stages
    .filter(stage => !stage.is_deleted_from_CRM)
    .map(stage => ({ label: stage.name, value: stage.name }));

  // Add note creation state
  const [isCreateNoteVisible, setIsCreateNoteVisible] = useState(false);
  const [noteBody, setNoteBody] = useState('');
  const [noteHeader, setNoteHeader] = useState('');
  const [noteLoading, setNoteLoading] = useState(false);

  // Handler to open note creation
  const openCreateNote = () => {
    setIsCreateNoteVisible(true);
    setNoteBody('');
    setNoteHeader('');
  };

  // Handler to submit note
  const handleCreateNote = async () => {
    if (!noteBody.trim() || !selectedLead) {
      message.error('Note body is required');
      return;
    }
    setNoteLoading(true);
    try {
      await fetch(
        `${apiUrl}/api/agent/${agentId}/lead/${selectedLead.source_id}/notes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ body: noteBody, header: noteHeader }),
        },
      );
      message.success('Note created');
      setIsCreateNoteVisible(false);
      // Refresh notes for this lead
      const response = await fetch(
        `${apiUrl}/api/agent/${agentId}/lead/${selectedLead.source_id}/notes`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      const notes = await response.json();
      setSelectedLead({ ...selectedLead, notes });
      setVisibleNotesCount(15);
    } catch (err) {
      message.error('Failed to create note');
    } finally {
      setNoteLoading(false);
    }
  };

  const fetchLeads = async (page = currentPage, size = pageSize) => {
    setLoading(true);
    setError(undefined);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: size.toString(),
      });

      // Add search parameter if search term exists
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      const response = await fetch(
        `${apiUrl}/api/agent/leads/${agentId}?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to fetch leads.');
      }

      const data = await response.json();
      setLeads(data.leads || []);
      setTotal(data.total || 0);
      setCurrentPage(data.page || 1);
      setPageSize(data.pageSize || size);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      setLeads([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (record: Lead) => {
    try {
      const response = await fetch(
        `${apiUrl}/api/agent/${agentId}/lead/${record.source_id}/notes`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch notes: ${response.statusText}`);
      }

      const notes = await response.json();
      setSelectedLead({ ...record, notes });
      setVisibleNotesCount(15);
      setIsModalVisible(true);
    } catch (err) {
      console.error(err);
      setError(
        err instanceof Error ? err.message : 'Unknown error fetching notes',
      );
    }
  };

  const handleStageChange = async (record: Lead, stage: string) => {
    setSavingStageLeadId(record.id);
    try {
      const response = await fetch(`${apiUrl}/api/leads/${record.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ stage }),
      });
      if (!response.ok) {
        throw new Error((await response.text()) || 'Failed to update stage.');
      }
      // The endpoint reports refusals as HTTP 200 with { error: true, message }.
      const data = await response.json();
      if (data.error) {
        throw new Error(data.message || 'Failed to update stage.');
      }
      setLeads(prev =>
        prev.map(lead =>
          lead.id === record.id ? { ...lead, stage: data.stage } : lead,
        ),
      );
      void message.success(`Stage updated to ${data.stage}`);
    } catch (err) {
      void message.error(
        err instanceof Error ? err.message : 'Failed to update stage.',
      );
    } finally {
      setSavingStageLeadId(null);
    }
  };

  useEffect(() => {
    if (!leads.length) {
      return;
    }
    const leadId = searchParams.get('lead');
    if (leadId) {
      const found = leads.find(l => l.id === leadId || l.source_id === leadId);
      if (found) {
        // Clear the lead param from the URL after "handling" it (finding it in results)
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('lead');
        setSearchParams(newParams);
      } else if (searchTerm !== leadId) {
        setSearchTerm(leadId);
      }
    }
  }, [leads, location.search]);

  // Add effect to refetch leads when search term changes
  useEffect(() => {
    if (agentId && accessToken) {
      void fetchLeads(1, pageSize); // Reset to first page when search changes
    }
  }, [searchTerm, agentId, accessToken]);

  useEffect(() => {
    if (agentId && accessToken) {
      void fetchLeads(1, pageSize); // Reset to first page on agent change
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, accessToken]);

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedLead(null);
    setVisibleNotesCount(15);
  };

  const columns: ColumnsType<Lead> = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => {
        const crmLink = getCrmLink(clientConfig, record.source_id);
        return (
          <Typography.Text>
            {record.source_id && crmLink ? (
              <a href={crmLink} rel="noopener noreferrer" target="_blank">
                {record.first_name} {record.last_name}
              </a>
            ) : (
              `${record.first_name} ${record.last_name}`
            )}
          </Typography.Text>
        );
      },
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: phone => <Typography.Text>{phone || '-'}</Typography.Text>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: email => <Typography.Text>{email || '-'}</Typography.Text>,
    },
    {
      title: 'Stage',
      dataIndex: 'stage',
      key: 'stage',
      render: (stage, record) => (
        <Select
          aria-label={`Stage for ${record.first_name} ${record.last_name}`}
          disabled={savingStageLeadId !== null}
          loading={savingStageLeadId === record.id}
          onChange={value => void handleStageChange(record, value)}
          options={stageOptions}
          placeholder="-"
          popupMatchSelectWidth={false}
          showSearch
          style={{ minWidth: 180 }}
          value={stage || undefined}
        />
      ),
    },
    {
      title: 'Notes',
      key: 'notes',
      render: () => (
        <Tooltip title="View Notes">
          <FileTextOutlined style={{ color: '#1890ff', fontSize: 18 }} />
        </Tooltip>
      ),
      onCell: record => ({
        onClick: () => handleRowClick(record),
        style: { cursor: 'pointer', textAlign: 'center' },
      }),
    },
  ];

  return (
    <>
      <Space
        style={{
          marginBottom: 16,
          justifyContent: 'space-between',
          display: 'flex',
          flexWrap: 'wrap',
        }}
      >
        <Input.Search
          allowClear
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search by name, email, or phone"
          style={{ width: 300 }}
          value={searchTerm}
        />
        <Button
          loading={loading}
          onClick={() => fetchLeads(currentPage, pageSize)}
        >
          Refresh Leads
        </Button>
      </Space>

      <Table
        bordered
        columns={columns}
        dataSource={leads}
        loading={loading}
        pagination={{
          size: 'small',
          position: ['bottomRight'],
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          pageSize: pageSize,
          current: currentPage,
          total: total,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
            fetchLeads(page, size);
          },
          showQuickJumper: true,
          showLessItems: true,
        }}
        rowKey="id"
        size="middle"
      />

      <Modal
        footer={null}
        onCancel={handleModalClose}
        open={isModalVisible}
        title="Lead Notes"
        width={700}
      >
        {selectedLead ? (
          <div>
            <Button
              icon={<CopyOutlined />}
              onClick={() => {
                let url = window.location.href;
                // Remove any existing lead param
                url = url.replace(/[&?]lead=[^&]*/g, '');
                // Add correct separator
                const hasQuery = url.includes('?');
                const sep = hasQuery
                  ? url.endsWith('?') || url.endsWith('&')
                    ? ''
                    : '&'
                  : '?';
                const textToCopy = `${url}${sep}lead=${selectedLead.source_id}`;
                navigator.clipboard.writeText(textToCopy).then(() => {
                  message.success('Link copied to clipboard');
                });
              }}
              size="small"
              style={{ marginBottom: 16, float: 'right' }}
            >
              Copy Note Link
            </Button>
            <Button
              onClick={openCreateNote}
              style={{ marginBottom: 16, float: 'left' }}
              type="primary"
            >
              Create Note
            </Button>
            <div style={{ clear: 'both' }} />
            {isCreateNoteVisible && (
              <div
                style={{
                  marginBottom: 16,
                  background: '#f6f6f6',
                  padding: 16,
                  borderRadius: 8,
                }}
              >
                <Input
                  onChange={e => setNoteHeader(e.target.value)}
                  placeholder="Header (optional)"
                  style={{ marginBottom: 8 }}
                  value={noteHeader}
                />
                <Input.TextArea
                  onChange={e => setNoteBody(e.target.value)}
                  placeholder="Note body"
                  rows={4}
                  style={{ marginBottom: 8 }}
                  value={noteBody}
                />
                <Button
                  loading={noteLoading}
                  onClick={handleCreateNote}
                  style={{ marginRight: 8 }}
                  type="primary"
                >
                  Submit Note
                </Button>
                <Button
                  disabled={noteLoading}
                  onClick={() => setIsCreateNoteVisible(false)}
                >
                  Cancel
                </Button>
              </div>
            )}
            {selectedLead.notes ? (
              Array.isArray(selectedLead.notes) ? (
                <>
                  {selectedLead.notes
                    .slice(0, visibleNotesCount)
                    .map((note: Note) => {
                      // Declare these variables only once per note
                      const header = note.header || note.subject;
                      const subjectHasHtml = header ? isHTML(header) : false;
                      const bodyHasHtml = note.body ? isHTML(note.body) : false;
                      return (
                        <div
                          key={note.id || Math.random()}
                          style={{
                            padding: '12px',
                            marginBottom: '16px',
                            border: '1px solid #f0f0f0',
                            borderRadius: '6px',
                            background: '#fafafa',
                            position: 'relative',
                          }}
                        >
                          {header && header.trim() && (
                            <Typography.Title
                              level={5}
                              style={{ marginBottom: 8 }}
                            >
                              {subjectHasHtml ? (
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: sanitizeHTML(header),
                                  }}
                                />
                              ) : (
                                header
                              )}
                            </Typography.Title>
                          )}
                          {bodyHasHtml ? (
                            <Typography.Paragraph>
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: sanitizeHTML(note.body),
                                }}
                              />
                            </Typography.Paragraph>
                          ) : (
                            <Typography.Paragraph
                              style={{ whiteSpace: 'pre-wrap' }}
                            >
                              {note.body}
                            </Typography.Paragraph>
                          )}
                          <Typography.Text type="secondary">
                            <strong>Note ID:</strong> {note.id || 'Unknown'}
                          </Typography.Text>
                          <br />
                          <Typography.Text type="secondary">
                            <strong>Created on</strong>{' '}
                            {note.created_at
                              ? new Date(note.created_at).toLocaleString()
                              : 'Unknown'}
                          </Typography.Text>
                          <br />
                          <Typography.Text type="secondary">
                            <strong>Updated on</strong>{' '}
                            {note.updated_at
                              ? new Date(note.updated_at).toLocaleString()
                              : 'Unknown'}
                          </Typography.Text>
                        </div>
                      );
                    })}
                  {selectedLead.notes.length > visibleNotesCount && (
                    <Button
                      onClick={() =>
                        setVisibleNotesCount(prev =>
                          Math.min(prev + 15, selectedLead.notes!.length),
                        )
                      }
                      type="link"
                    >
                      Show more notes
                    </Button>
                  )}
                </>
              ) : (
                <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                  {selectedLead.notes}
                </Typography.Paragraph>
              )
            ) : (
              <Typography.Text type="secondary">
                No notes available for this lead.
              </Typography.Text>
            )}
          </div>
        ) : (
          <Typography.Text type="secondary">No lead selected.</Typography.Text>
        )}
      </Modal>
    </>
  );
}
